/**
 * HealthBadgeWithTooltip Component
 * Story: E007-S009 - Campaign Health Score
 * 
 * Enhanced health badge with rich breakdown tooltip showing component scores.
 */

import React, { useState } from 'react';

export interface HealthBreakdown {
  roas_score?: number;
  ctr_score?: number;
  pacing_score?: number;
  engagement_score?: number;
}

interface HealthBadgeWithTooltipProps {
  score: number | null;
  breakdown?: HealthBreakdown | null;
  trend?: 'improving' | 'declining' | 'stable' | 'unknown' | null;
  trendChange?: number | null;
  size?: 'sm' | 'md' | 'lg';
}

const HealthBadgeWithTooltip: React.FC<HealthBadgeWithTooltipProps> = ({ 
  score, 
  breakdown,
  trend,
  trendChange,
  size = 'sm'
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Handle null/undefined scores
  if (score === null || score === undefined) {
    return (
      <span 
        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
        title="Health score calculating..."
      >
        N/A
      </span>
    );
  }

  // Determine category and color
  const getHealthInfo = (score: number) => {
    if (score >= 80) {
      return {
        category: 'Excellent',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        textColor: 'text-green-800 dark:text-green-300',
        dotColor: 'bg-green-500',
        borderColor: 'border-green-200 dark:border-green-800'
      };
    } else if (score >= 60) {
      return {
        category: 'Good',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        textColor: 'text-yellow-800 dark:text-yellow-300',
        dotColor: 'bg-yellow-500',
        borderColor: 'border-yellow-200 dark:border-yellow-800'
      };
    } else if (score >= 40) {
      return {
        category: 'Fair',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        textColor: 'text-orange-800 dark:text-orange-300',
        dotColor: 'bg-orange-500',
        borderColor: 'border-orange-200 dark:border-orange-800'
      };
    } else {
      return {
        category: 'Poor',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        textColor: 'text-red-800 dark:text-red-300',
        dotColor: 'bg-red-500',
        borderColor: 'border-red-200 dark:border-red-800'
      };
    }
  };

  const healthInfo = getHealthInfo(score);

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  // Trend icon and color
  const getTrendIcon = () => {
    if (!trend || trend === 'unknown') return null;
    
    switch (trend) {
      case 'improving':
        return <span className="text-green-500 ml-1">↑</span>;
      case 'declining':
        return <span className="text-red-500 ml-1">↓</span>;
      case 'stable':
        return <span className="text-gray-400 ml-1">→</span>;
      default:
        return null;
    }
  };

  // Progress bar for breakdown scores
  const ScoreBar = ({ label, value, maxLabel }: { label: string; value?: number; maxLabel?: string }) => {
    const displayValue = value ?? 0;
    const barColor = displayValue >= 80 ? 'bg-green-500' : displayValue >= 60 ? 'bg-yellow-500' : displayValue >= 40 ? 'bg-orange-500' : 'bg-red-500';
    
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="w-24 text-gray-600 dark:text-gray-400">{label}</span>
        <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-full ${barColor} transition-all duration-300`}
            style={{ width: `${displayValue}%` }}
          />
        </div>
        <span className="w-12 text-right text-gray-700 dark:text-gray-300 font-medium">
          {displayValue}/100
        </span>
      </div>
    );
  };

  return (
    <div className="relative inline-block">
      <span
        className={`
          inline-flex items-center gap-1 rounded-full font-semibold cursor-pointer
          ${healthInfo.bgColor} ${healthInfo.textColor} ${sizeClasses[size]}
          transition-all hover:scale-105
        `}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        data-testid="health-badge"
      >
        <span className={`w-2 h-2 rounded-full ${healthInfo.dotColor}`} />
        <span>{score}</span>
        {getTrendIcon()}
      </span>

      {/* Rich Tooltip */}
      {showTooltip && (
        <div 
          className={`
            absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2
            w-64 p-3 rounded-lg shadow-xl
            bg-white dark:bg-gray-800 border ${healthInfo.borderColor}
            animate-in fade-in duration-200
          `}
          data-testid="health-tooltip"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
            <span className="font-semibold text-gray-900 dark:text-white">
              Health Score: {score}/100
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${healthInfo.bgColor} ${healthInfo.textColor}`}>
              {healthInfo.category}
            </span>
          </div>

          {/* Breakdown Scores */}
          {breakdown && (
            <div className="space-y-2 mb-3">
              <ScoreBar label="ROAS" value={breakdown.roas_score} />
              <ScoreBar label="CTR" value={breakdown.ctr_score} />
              <ScoreBar label="Pacing" value={breakdown.pacing_score} />
              <ScoreBar label="Engagement" value={breakdown.engagement_score} />
            </div>
          )}

          {/* Trend */}
          {trend && trend !== 'unknown' && (
            <div className="text-xs text-gray-600 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="flex items-center gap-1">
                Trend: 
                {trend === 'improving' && (
                  <span className="text-green-600 dark:text-green-400">
                    ↑ {trendChange ? `+${trendChange}` : 'Improving'} pts (7d)
                  </span>
                )}
                {trend === 'declining' && (
                  <span className="text-red-600 dark:text-red-400">
                    ↓ {trendChange ? trendChange : 'Declining'} pts (7d)
                  </span>
                )}
                {trend === 'stable' && (
                  <span className="text-gray-500">
                    → Stable (7d)
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Arrow */}
          <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white dark:border-t-gray-800`} />
        </div>
      )}
    </div>
  );
};

export default HealthBadgeWithTooltip;
